import type { TenantSimple } from '@repositories/tenantRepository/tenant.types';
import type { UserGroupBase } from '@repositories/userGroupsRepository/userGroups.types';
import type { Common } from '@type/Common';

type UserStatus = 'CREATED' | 'ACTIVE';

type UserRole = 'ROLE_ADMIN' | 'ROLE_USER' | 'ROLE_SALES' | 'ROLE_TAM' | 'ROLE_PARTNER';

// JWT roles claim에 포함되는 테넌트 역할
type TenantRole = 'TENANT_SUPER_ADMIN' | 'TENANT_GENERAL_ADMIN' | 'TENANT_EXTERNAL_ADMIN' | 'TENANT_SUBSCRIPTION_OWNER';

// 어드민 콘솔(매니저 페이지) 접근 가능한 테넌트 역할
// 구독 관리자(TENANT_SUBSCRIPTION_OWNER)도 포함 - 단, 구독 메뉴만 접근 가능
const ADMIN_CONSOLE_ACCESS_ROLES: TenantRole[] = [
  'TENANT_SUPER_ADMIN',
  'TENANT_GENERAL_ADMIN',
  'TENANT_EXTERNAL_ADMIN',
  'TENANT_SUBSCRIPTION_OWNER',
];

// 전체 메뉴 접근 가능한 관리자 역할 (구독 관리자 제외)
const FULL_MENU_ACCESS_ROLES: TenantRole[] = ['TENANT_SUPER_ADMIN', 'TENANT_GENERAL_ADMIN', 'TENANT_EXTERNAL_ADMIN'];

type User = Common & {
  accessToken: string;
  additionalPermissions: AdditionalPermission[];
  address: string;
  companyId: string;
  email: string;
  id: string;
  isDeleted: boolean;
  lastLoginedAt: string;
  name: string;
  phone: string;
  roles: UserRole[];
  username: string;
  sources: string[];
  status: UserStatus;
  mfaEnabled: boolean;
  mfaQrCodeUrl: string;
  mfaRequired: boolean;
  mfaSecret: string;
  mfaSetupAt: DateTimeString;
  mfaSetupRequired: boolean;
  lastPasswordChangeDate: DateTimeString;
  limitedToken?: boolean;
};

type Profile = {
  address: string;
  email: string;
  id: string;
  lastLoginedAt: DateTimeString;
  lastPasswordChangeDate: DateTimeString;
  name: string;
  phone: string;
  roles: string;
  tenants: TenantSimple[];
  userGroups: UserGroupBase[];
  username: string;
  mfaEnabled: boolean;
  mfaSetupAt: string | null;
};

type UserSimple = {
  id: UserId;
  username: string;
  name: string;
  phone: string;
  email: string;
  status: UserStatus;
};

type UserCreateRq = {
  address?: string;
  companyId?: string;
  email?: string;
  name: string;
  password: string;
  phone?: string;
  roles?: UserRole[];
  source?: string;
  username: string; // ID, email
};

type UserUpdateRq = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  companyId?: string;
  roles: string[];
};

type Member = {
  memberRole: 'GENERAL_ADMIN' | 'EXTERNAL_ADMIN' | 'USER';
  user: User;
};

type ChangePassword = {
  userId: string;
  password: string;
};

type TenantMemberSimple = {
  createdDate?: string;
  updatedDate?: string;
  deleted?: boolean;
  deletedDate?: string;
  email?: string;
  id: string;
  lastLoginedAt?: string;
  memberRole?: Permission;
  name: string;
  phone?: string;
  subscriptionCount?: number;
  status?: MemberStatus;
  tenantMemberStatus?: TenantMemberStatus;
  userGroups?: string;
  username?: string;
  customRole?: string;
  smpSubscription?: boolean;
  mfaEnabled?: boolean;
};

type TenantMemberStatus = 'ACTIVE' | 'BLOCKED';
type MemberStatus = 'ACTIVE' | 'CREATED';

type UserSignInRq = {
  username: string;
  password: string;
};

type AdditionalPermission = {
  resourceType: 'TENANT' | 'SUBSCRIPTION' | 'PRODUCT' | 'COMPANY' | 'PRODUCT_PARTNER';
  resourceId: string;
  permission: Permission;
};

type Permission = 'SUPER_ADMIN' | 'GENERAL_ADMIN' | 'EXTERNAL_ADMIN' | 'ADMIN' | 'WRITE' | 'USER' | 'READ' | 'SUBSCRIPTION_LIST_READ';

type UserFilter = {
  keyword?: string;
  username?: string;
  address?: string;
  companyId?: string;
  email?: string;
  isDeleted?: boolean;
  name?: string;
  phone?: string;
  role?: UserRole;
  page?: number;
  size?: number;
  sort?: string[];
};

type ProfileUpdateRq = {
  address?: string;
  email?: string;
  name?: string;
  phone?: string;
  customRole?: string;
  userGroupIdList?: string[];
};

type UserInviteRq = {
  isMailSend?: boolean;
  memberRole?: Permission;
  email: string;
  phone?: string;
  userGroupIdList?: string[];
  userGroupCodeList?: string[];
  username: string;
  tenantId: string;
  isExistingUserUpdated?: boolean;
  customRole?: string;
};

type UserStatusUpdateRq = {
  status: TenantMemberStatus;
  userId: string;
};

type PasswordInitialUpdateRq = {
  activeCode: string;
  password: string;
};

type UserPasswordRecoveryDTO = {
  email: string;
};

type UserPermissionCreateRq = {
  resourceType: AdditionalPermission['resourceType'];
  resourceId: string;
  permission: Permission;
};

type UserRoleUpdateRq = {
  userId: string;
  roles: UserRole[];
};

type UserEmailAuthResponse = {
  accessToken: string;
};

type UserInviteRes = {
  result: boolean;
};

export type {
  User,
  UserStatus,
  UserSimple,
  UserCreateRq,
  UserUpdateRq,
  UserSignInRq,
  Member,
  UserRole,
  TenantRole,
  AdditionalPermission,
  TenantMemberSimple,
  UserFilter,
  Profile,
  ChangePassword,
  ProfileUpdateRq,
  UserInviteRq,
  Permission,
  MemberStatus,
  UserStatusUpdateRq,
  PasswordInitialUpdateRq,
  TenantMemberStatus,
  UserPasswordRecoveryDTO,
  UserPermissionCreateRq,
  UserRoleUpdateRq,
  UserEmailAuthResponse,
  UserInviteRes,
};

export { ADMIN_CONSOLE_ACCESS_ROLES, FULL_MENU_ACCESS_ROLES };
